import axios from "axios";
import {
  IPedido,
  IPetForBackend,
  IPetPopulated,
  IPets,
  Vendas,
  ILancamento,
  INotification,
  ICreateNotification,
  IUpdateNotification,
  INotificationResponse,
  INotificationFilters,
} from "../types";

const API_URL = process.env.REACT_APP_API_FP_BE;

export interface PedidoSearchParams {
  cpfcnpj?: string;
  cliente?: string;
}

interface ApiUserData {
  id: string;
  nome: string;
  cpfcnpj: string;
  // outros campos que a API retorna
}

// Interface para a resposta da API
interface LoginResponse {
  token: string;
  userId: string;
  apiUserData?: ApiUserData; // Dados opcionais da API externa
}

// PET
// Monta o payload completo conforme o exemplo da API-
// busca todos os pets ou por usuário
export const getPets = async (userId?: string) => {
  try {
    const url = userId ? `${API_URL}/pets/user/${userId}` : `${API_URL}/pets`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      timeout: 30000,
    });

    if (!response.data) {
      throw new Error("Resposta vazia do servidor");
    }

    return response.data;
  } catch (error) {
    console.error("Erro detalhado:", error);
  }
};

export const createPet = async (
  petData: IPetForBackend
): Promise<IPetForBackend> => {
  try {
    const response = await axios.post(`${API_URL}/pets`, petData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("❌ Erro ao criar pet:", error);
    throw error;
  }
};

export const updatePet = async (
  petId: string,
  petData: IPetPopulated
): Promise<IPets> => {
  try {
    const response = await axios.put(`${API_URL}/pets/${petId}`, petData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("❌ Erro ao atualizar pet:", error);
    throw error;
  }
};

export const deletePet = async (petId: string): Promise<void> => {
  try {
    await axios.delete(`${API_URL}/pets/${petId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  } catch (error) {
    console.error("Erro ao deletar pet:", error);
    throw error;
  }
};

// Atualiza a função de login
export const handleLogin = async (
  login: string,
  password: string
): Promise<LoginResponse> => {
  try {
    const response = await axios.post<LoginResponse>(
      `${API_URL}/login`,
      { login, password },
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );

    localStorage.setItem("token", response.data.token);
    localStorage.setItem("userERP", JSON.stringify(response.data.apiUserData));
    localStorage.setItem("userId", JSON.stringify(response.data.userId));
    localStorage.setItem("toolpad-mode", "light");

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Erro na resposta:", error.response?.data);
      throw new Error(error.response?.data?.message || "Erro ao fazer login");
    }
    console.error("Erro ao fazer login:", error);
    throw new Error("Erro inesperado ao fazer login");
  }
};

// PEDIDOS -----------
export const getPedidos = async (): Promise<IPedido[]> => {
  try {
    // Usar a rota /pedidos/user sem parâmetros para buscar todos
    const response = await axios.get<IPedido[]>(`${API_URL}/pedidos/user`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Detalhes do erro:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw new Error(
        error.response?.data?.message || "Erro ao buscar pedidos"
      );
    }
    console.error("Erro inesperado:", error);
    throw new Error("Erro desconhecido ao buscar pedidos");
  }
};

export const getPedidosByUser = async (params: PedidoSearchParams) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.cpfcnpj) queryParams.append("cpfcnpj", params.cpfcnpj);
    if (params.cliente) queryParams.append("cliente", params.cliente);

    const response = await axios.get(
      `${API_URL}/pedidos/user?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        timeout: 30000,
      }
    );

    if (!response.data) {
      throw new Error("Resposta vazia do servidor");
    }

    return response.data as IPedido[];
  } catch (error) {
    console.error("Erro ao buscar pedidos:", error);
    throw error;
  }
};

export const getPedidosByTech = async (params: PedidoSearchParams) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.cliente) queryParams.append("cliente", params.cliente);
    const response = await axios.get(
      `${API_URL}/pedidos/user?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        timeout: 30000,
      }
    );

    if (!response.data) {
      throw new Error("Resposta vazia do servidor");
    }

    return response.data as IPedido[];
  } catch (error) {
    console.error("Erro ao buscar pedidos:", error);
    throw error;
  }
};

export const createAttachment = async (
  attachmentData: IPedido
): Promise<IPedido> => {
  try {
    const response = await axios.post(
      `${API_URL}/create-attachment`,
      attachmentData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        timeout: 30000,
      }
    );

    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Erro ao criar pedido");
    }
  } catch (error: any) {
    console.error("Erro ao criar anexo:", error);

    // Tratamento específico por tipo de erro
    if (error.response?.data) {
      const errorData = error.response.data;
      console.error("Detalhes do erro:", errorData);

      switch (errorData.code) {
        case "EXTERNAL_API_ERROR":
          console.error("Erro na API externa:", errorData.externalError);
          break;
        case "NETWORK_ERROR":
          console.error("Problema de rede/conexão");
          break;
        case "INTERNAL_ERROR":
          console.error("Erro interno do servidor");
          break;
      }

      throw new Error(errorData.message || "Erro ao criar pedido");
    } else if (error.code === "ECONNABORTED") {
      console.error("Timeout - servidor demorou muito para responder");
      throw new Error("Timeout - servidor demorou muito para responder");
    } else {
      console.error("Erro de conexão:", error.message);
      throw new Error("Erro de conexão");
    }
  }
};

// Upload de imagem
export const uploadImage = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append("foto", file);
  const res = await axios.post(`${API_URL}/upload`, formData, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data.imageUrl;
};

// USUÁRIOS -----------
// Buscar todos os Users
export const getServices = async () => {
  try {
    const url = `${API_URL}/services`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      timeout: 30000, // 10 segundos timeout
    });

    if (!response.data) {
      throw new Error("Resposta vazia do servidor");
    }

    return response.data;
  } catch (error) {
    console.error("Erro detalhado:", error);
  }
};

export const getProdutos = async () => {
  try {
    const url = `${API_URL}/produtos`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      timeout: 30000, // 10 segundos timeout
    });

    if (!response.data) {
      throw new Error("Resposta vazia do servidor");
    }

    return response.data;
  } catch (error) {
    console.error("Erro detalhado:", error);
  }
};

export const getUsers = async () => {
  try {
    const url = `${API_URL}/get-all`;
    const token = localStorage.getItem("token");

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.data) {
      throw new Error("Resposta vazia do servidor");
    }

    return response.data;
  } catch (error) {
    console.error("getUsers - Erro detalhado:", {
      message: (error as any).message,
      status: (error as any).response?.status,
      url: (error as any).config?.url,
      data: (error as any).response?.data,
    });
    throw error;
  }
};

export const searchUsers = async (
  searchTerm: string,
  pageSize: number = 500,
  skip: number = 0
) => {
  try {
    if (!searchTerm || searchTerm.trim() === "") {
      return [];
    }

    const url = `${API_URL}/search-users`;
    const token = localStorage.getItem("token");

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        q: searchTerm.trim(),
        pageSize,
        skip,
      },
    });

    if (!response.data) {
      return [];
    }

    return response.data;
  } catch (error) {
    console.error("searchUsers - Erro detalhado:", {
      message: (error as any).message,
      status: (error as any).response?.status,
      url: (error as any).config?.url,
      data: (error as any).response?.data,
    });
    throw error;
  }
};

export const createUser = async (tutorData: any): Promise<any> => {
  try {
    const response = await axios.post(`${API_URL}/create-user`, tutorData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("❌ Erro ao criar usuário:", error);
    throw error;
  }
};

// Função para atualizar tutor (sincroniza com API externa)
export const updateTutor = async (
  userId: string,
  tutorData: any
): Promise<any> => {
  try {
    const response = await axios.put(
      `${API_URL}/users/${userId}/tutor`,
      tutorData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("❌ Erro ao atualizar tutor:", error);
    throw error;
  }
};

// Função para criar lançamento financeiro
export const createLancamento = async (
  lancamentoData: ILancamento
): Promise<any> => {
  try {
    const response = await axios.post(
      `${API_URL}/create-lancamento`,
      lancamentoData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        timeout: 30000,
      }
    );

    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Erro ao criar lançamento");
    }
  } catch (error: any) {
    console.error("Erro ao criar lançamento:", error);

    // Tratamento específico por tipo de erro
    if (error.response?.data) {
      const errorData = error.response.data;

      switch (errorData.code) {
        case "EXTERNAL_API_ERROR":
          console.error("Erro na API externa:", errorData.externalError);
          break;
        case "NETWORK_ERROR":
          console.error("Problema de rede/conexão");
          break;
        case "INTERNAL_ERROR":
          console.error("Erro interno do servidor");
          break;
      }

      throw new Error(errorData.message || "Erro ao criar lançamento");
    } else if (error.code === "ECONNABORTED") {
      throw new Error("Timeout - servidor demorou muito para responder");
    } else {
      throw new Error("Erro de conexão");
    }
  }
};

// Função para criar lançamento a partir de um pedido existente
export const createLancamentoFromPedido = async (pedido: any): Promise<any> => {
  try {
    const response = await axios.post(
      `${API_URL}/create-lancamento-from-pedido`,
      { pedido },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        timeout: 30000,
      }
    );

    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Erro ao criar lançamento");
    }
  } catch (error: any) {
    console.error("Erro ao criar lançamento a partir do pedido:", error);

    if (error.response?.data) {
      throw new Error(
        error.response.data.message || "Erro ao criar lançamento"
      );
    } else if (error.code === "ECONNABORTED") {
      throw new Error("Timeout - servidor demorou muito para responder");
    } else {
      throw new Error(error.message || "Erro de conexão");
    }
  }
};

// Função para buscar lançamentos por número do documento
export const getLancamentosByDocumento = async (numeroDocumento: string) => {
  try {
    const response = await axios.get(`${API_URL}/external/lancamentos/search`, {
      params: {
        documento: numeroDocumento,
        filtrarNumeroDocumentoExato: true,
        pageSize: 100,
      },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar lançamentos:", error);
    throw error;
  }
};

// Função para obter detalhes de um lançamento específico
export const getLancamentoById = async (codigo: number) => {
  try {
    const response = await axios.get(`${API_URL}/external/lancamentos/get`, {
      params: {
        codigo: codigo,
      },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar lançamento:", error);
    throw error;
  }
};

// Função para adicionar pagamento a um lançamento
export const adicionarPagamentoLancamento = async (
  codigo: number,
  pagamentos: Array<{
    data: string;
    formaPagamento: string;
    numeroDocumento?: string;
    contaBancaria?: string;
    conciliado: boolean;
    valor: number;
  }>
) => {
  try {
    const response = await axios.post(
      `${API_URL}/external/lancamentos/adicionar-pagamentos`,
      {
        codigo: codigo,
        pagamentos: pagamentos,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        timeout: 30000,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao adicionar pagamento:", error);
    throw error;
  }
};

// Função para atualizar observações de um lançamento
export const atualizarLancamento = async (
  codigo: number,
  dadosAtualizacao: any
) => {
  try {
    const response = await axios.put(
      `${API_URL}/external/lancamentos/atualizar`,
      {
        codigo: codigo,
        ...dadosAtualizacao,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        timeout: 30000,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao atualizar lançamento:", error);
    throw error;
  }
};

// Função para salvar um pedido na API externa
export const salvarPedido = async (
  pedidoCompleto: any,
  novaDescricao: string
) => {
  try {
    // Pedidos antigos do ERP têm campos adicionais (pagamento, ordem de
    // serviço, endereço etc.) que precisam ser preservados em uma edição.
    // A única alteração funcional deste fluxo é a observação do pedido.
    const payloadCompleto: any = {
      ...pedidoCompleto,
      codigo: Number(pedidoCompleto.codigo || pedidoCompleto.Codigo),
      vendedor: pedidoCompleto.vendedor || pedidoCompleto.Vendedor,
      Cliente:
        pedidoCompleto.cliente ||
        pedidoCompleto.Cliente ||
        pedidoCompleto.NomeCliente,
      Empresa: pedidoCompleto.Empresa || "PETSHOP DA MADRE",
      Deposito: pedidoCompleto.Deposito || "PADRÃO",
      // Não modificar a descrição dos itens ao editar somente a observação.
      Items: pedidoCompleto.Items || pedidoCompleto.items || [],
      Descricao: novaDescricao,
    };

    // A API de salvamento já funciona com estas chaves em minúsculo. Remover
    // os aliases evita enviar o mesmo campo duas vezes com capitalização distinta.
    delete payloadCompleto.Codigo;
    delete payloadCompleto.Vendedor;
    delete payloadCompleto.items;

    const response = await axios.put(
      `${API_URL}/external/pedidos/salvar`,
      payloadCompleto,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        timeout: 30000,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao salvar pedido:", error);
    throw error;
  }
};

// Função para excluir um pedido na API externa
export const excluirPedido = async (codigoPedido: number) => {
  try {
    const response = await axios.delete(`${API_URL}/external/pedidos/excluir`, {
      params: { codigo: codigoPedido },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      timeout: 30000,
    });

    return response.data;
  } catch (error) {
    console.error("Erro ao excluir pedido:", error);
    throw error;
  }
};

// Função para excluir um lançamento na API externa
export const excluirLancamento = async (codigoLancamento: number) => {
  try {
    const response = await axios.delete(
      `${API_URL}/external/lancamentos/excluir`,
      {
        params: { codigo: codigoLancamento },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        timeout: 30000,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao excluir lançamento:", error);
    throw error;
  }
};

// Pesquisar serviços na API externa
export const searchExternalServices = async (nome: string) => {
  try {
    if (!nome || nome.trim().length < 2) {
      return [];
    }

    const url = `${API_URL}/external/services/search?nome=${encodeURIComponent(nome)}`;

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      timeout: 30000,
    });
    return response.data || [];
  } catch (error) {
    console.error("Erro ao pesquisar serviços externos:", error);
    return [];
  }
};

// Pesquisar produtos na API externa (todos os tipos)
export const searchExternalProducts = async (nome: string) => {
  try {
    if (!nome || nome.trim().length < 2) {
      return [];
    }

    const url = `${API_URL}/external/products/search?nome=${encodeURIComponent(nome)}`;

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      timeout: 30000,
    });
    return response.data || [];
  } catch (error) {
    console.error("Erro ao pesquisar produtos externos:", error);
    return [];
  }
};

// NOTIFICATIONS -----------

// Criar nova notificação
export const createNotification = async (
  notificationData: ICreateNotification
): Promise<INotification> => {
  try {
    const response = await axios.post(
      `${API_URL}/notifications`,
      notificationData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erro ao criar notificação");
    }
  } catch (error: any) {
    console.error("Erro ao criar notificação:", error);
    throw error;
  }
};

// Buscar notificações com filtros e paginação
export const getNotifications = async (
  filters?: INotificationFilters
): Promise<INotificationResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.isRead !== undefined) {
      params.append("isRead", filters.isRead.toString());
    }
    if (filters?.isActive !== undefined) {
      params.append("isActive", filters.isActive.toString());
    }
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);

    const response = await axios.get(
      `${API_URL}/notifications?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("Erro ao buscar notificações:", error);
    throw error;
  }
};

// Buscar notificação por ID
export const getNotificationById = async (
  id: string
): Promise<INotification> => {
  try {
    const response = await axios.get(`${API_URL}/notifications/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Notificação não encontrada");
    }
  } catch (error: any) {
    console.error("Erro ao buscar notificação:", error);
    throw error;
  }
};

// Atualizar notificação
export const updateNotification = async (
  id: string,
  updateData: IUpdateNotification
): Promise<INotification> => {
  try {
    const response = await axios.put(
      `${API_URL}/notifications/${id}`,
      updateData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erro ao atualizar notificação");
    }
  } catch (error: any) {
    console.error("Erro ao atualizar notificação:", error);
    throw error;
  }
};

// Marcar notificação como lida
export const markNotificationAsRead = async (id: string): Promise<void> => {
  try {
    const response = await axios.patch(
      `${API_URL}/notifications/${id}/read`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Erro ao marcar como lida");
    }
  } catch (error: any) {
    console.error("Erro ao marcar notificação como lida:", error);
    throw error;
  }
};

// Marcar todas as notificações como lidas
export const markAllNotificationsAsRead = async (): Promise<void> => {
  try {
    const response = await axios.patch(
      `${API_URL}/notifications/read-all`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (!response.data.success) {
      throw new Error(
        response.data.message || "Erro ao marcar todas como lidas"
      );
    }
  } catch (error: any) {
    console.error("Erro ao marcar todas as notificações como lidas:", error);
    throw error;
  }
};

// Deletar notificação
export const deleteNotification = async (id: string): Promise<void> => {
  try {
    const response = await axios.delete(`${API_URL}/notifications/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || "Erro ao deletar notificação");
    }
  } catch (error: any) {
    console.error("Erro ao deletar notificação:", error);
    throw error;
  }
};

// ===================== HISTORY SERVICE =====================

export interface IPetHistoryData {
  // Campos de Vermifugação e Parasitários
  ultimaVermifugacao?: string;
  marcaVermifugacao?: string;
  ultimoParasitario?: string;
  marcaParasitario?: string;
  ultimaAplicacao?: string;
  tipoAplicacao?: string;
  // Campos de Vacinas
  ultimaVacinaMultipla?: string;
  ultimaVacinaPuppy?: string;
  ultimaVacinaGiardia?: string;
  ultimaVacinaRaiva?: string;
  ultimaVacinaGripe?: string;
  // Campos de Alimentação
  primeiraRacao?: string;
  quantidadeRacao?: string;
  // Campo de Observações
  observacoes?: string;
}

export interface IHistory {
  id: string;
  petId: string;
  occurredAt: string;
  description?: string;
  responsible?: string;
  serviceType?: string;
  ultimaVermifugacao?: string;
  marcaVermifugacao?: string;
  ultimoParasitario?: string;
  marcaParasitario?: string;
  ultimaAplicacao?: string;
  tipoAplicacao?: string;
  ultimaVacinaMultipla?: string;
  ultimaVacinaPuppy?: string;
  ultimaVacinaGiardia?: string;
  ultimaVacinaRaiva?: string;
  ultimaVacinaGripe?: string;
  primeiraRacao?: string;
  quantidadeRacao?: string;
  observacoes?: string;
  createdAt: string;
  updatedAt: string;
}

// Criar ou atualizar informações de histórico do pet
export const createOrUpdatePetHistory = async (
  petId: string,
  historyData: IPetHistoryData
): Promise<IHistory> => {
  try {
    const response = await axios.post(
      `${API_URL}/history/${petId}`,
      historyData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return response.data.data || response.data;
  } catch (error: any) {
    console.error("Erro ao salvar histórico do pet:", error);
    throw error;
  }
};

// Buscar histórico do pet
export const getPetHistory = async (petId: string): Promise<IHistory[]> => {
  try {
    const response = await axios.get(`${API_URL}/history/${petId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    return response.data.data || response.data;
  } catch (error: any) {
    console.error("Erro ao buscar histórico do pet:", error);
    throw error;
  }
};

// Buscar o registro mais recente de histórico do pet
export const getLatestPetHistory = async (
  petId: string
): Promise<IHistory | null> => {
  try {
    const response = await axios.get(`${API_URL}/history/${petId}/latest`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    return response.data.data || response.data;
  } catch (error: any) {
    console.error("Erro ao buscar histórico mais recente do pet:", error);
    return null;
  }
};

// Buscar CEP na API Externa (ERP)
export interface ICepData {
  CEPNumero: string;
  Bairro: string;
  MunicipioCodigo: string;
  MunicipioNome: string;
  EstadoNome: string;
  EstadoUF: string;
  EstadoCodigoUF: string;
  Logradouro: string;
}

export const fetchCep = async (cep: string): Promise<ICepData | null> => {
  try {
    const response = await axios.get(`${API_URL}/cep/${cep}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      timeout: 10000,
    });

    return response.data;
  } catch (error: any) {
    console.error("Erro ao buscar CEP:", error);
    return null;
  }
};

// ==================== SCHEDULE (AGENDAMENTOS) ====================

// Buscar agendamentos com filtros opcionais
export const getSchedules = async (filters?: {
  petId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  requiresPickup?: boolean;
}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters?.petId) queryParams.append("petId", filters.petId);
    if (filters?.status) queryParams.append("status", filters.status);
    if (filters?.startDate) queryParams.append("startDate", filters.startDate);
    if (filters?.endDate) queryParams.append("endDate", filters.endDate);
    if (filters?.requiresPickup !== undefined)
      queryParams.append("requiresPickup", String(filters.requiresPickup));

    const url = `${API_URL}/schedules${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      timeout: 30000,
    });

    return response.data;
  } catch (error: any) {
    console.error("Erro ao buscar agendamentos:", error);
    throw error;
  }
};

// Buscar agendamentos por período (útil para calendário)
export const getSchedulesByPeriod = async (
  startDate: string,
  endDate: string
) => {
  try {
    const response = await axios.get(
      `${API_URL}/schedules/period?startDate=${startDate}&endDate=${endDate}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        timeout: 30000,
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("Erro ao buscar agendamentos por período:", error);
    throw error;
  }
};

// Buscar um agendamento específico por ID
export const getScheduleById = async (id: string) => {
  try {
    const response = await axios.get(`${API_URL}/schedules/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Erro ao buscar agendamento:", error);
    throw error;
  }
};

// Criar novo agendamento
export const createSchedule = async (scheduleData: {
  petId: string;
  serviceName: string;
  serviceExternalId?: string;
  scheduledAt: string | Date;
  status?: string;
  requiresPickup?: boolean;
  isRecurring?: boolean;
  recurrencePattern?: string;
  notes?: string;
  createNotification?: boolean; // Nova opção para criar notificação
}) => {
  try {
    const response = await axios.post(`${API_URL}/schedules`, scheduleData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Erro ao criar agendamento:", error);
    throw error;
  }
};

// Atualizar agendamento existente
export const updateSchedule = async (
  id: string,
  scheduleData: {
    petId?: string;
    serviceName?: string;
    serviceExternalId?: string;
    scheduledAt?: string | Date;
    status?: string;
    requiresPickup?: boolean;
    isRecurring?: boolean;
    recurrencePattern?: string;
    notes?: string;
    createNotification?: boolean; // Nova opção para criar/atualizar notificação
  }
) => {
  try {
    const response = await axios.put(
      `${API_URL}/schedules/${id}`,
      scheduleData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("Erro ao atualizar agendamento:", error);
    throw error;
  }
};

// Atualizar apenas o status do agendamento
export const updateScheduleStatus = async (id: string, status: string) => {
  try {
    const response = await axios.patch(
      `${API_URL}/schedules/${id}/status`,
      { status },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("Erro ao atualizar status do agendamento:", error);
    throw error;
  }
};

// Deletar agendamento
export const deleteSchedule = async (id: string) => {
  try {
    const response = await axios.delete(`${API_URL}/schedules/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Erro ao deletar agendamento:", error);
    throw error;
  }
};
